// Modules
import axios from "axios";
import { useEffect, useState } from "react";

// Environment variables
import { environment } from "../../environment";

// Components
import { ClockComponent } from "../clock/clock.component";
import { ErrorsComponent } from "../errors/errors.component";
import { LoaderComponent } from "../loader/loader.component";

// Types
import { ErrorsType } from "../errors/errors.type";
import {
  AuthProvider,
  Client,
  ClientProviders,
  LoginProperties,
} from "./login.properties";

// i18n
import i18next from "i18next";

// Styles
import "./login.styles.scss";
import { useTranslation } from "react-i18next";
import { UserHelper } from "../../helpers/UserHelper";
import MicrosoftButton from "./providers/microsoft.button";
import GoogleButton from "./providers/google.button";
import PasswordInput from "../password/password-input.component";
import { UserSettingsHelper } from "../../helpers/UserSettingsHelper";
import useParsedUrl from "../../hooks/useParsedUrl";
import { appEventEmitter } from '../../context/EventContext';
import UserListener from '../listeners/UserListener';

/**
 * Login Component
 * @description Component to login in the platform
 * @param {LoginProperties} properties
 * @returns {JSX.Element}
 */
export function LoginComponent(properties: LoginProperties): JSX.Element {
  // translation function
  const { t } = useTranslation();

  // errors of login request
  const [errors, setErrors] = useState<ErrorsType>({});

  // email of user
  const [email, setEmail] = useState<string>("");

  // password of user
  const [password, setPassword] = useState<string>("");

  const [enableProvider, setEnableProvider] = useState<boolean>(false);

  /*
      id -> auth_providers_group in hasura
      provider -> auth_providers in hasura
    */
  const [providers, setProviders] = useState<
    {
      id: ClientProviders["id"];
      name: AuthProvider["name"];
    }[]
  >([{ id: "", name: "local" }]);

  const [clients, setClients] = useState<Client[]>([]);

  // loading of axios request
  const [loading, setLoading] = useState<boolean>(false);

  const parsedUrl = useParsedUrl<["type"]>(window.location.href, ["type"]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalType, setModalType] = useState<"success" | "error" | "warning">(
    "success"
  );

    const [loginAttempts, setLoginAttempts] = useState(0);

  const onClose = () => {
    setIsModalOpen(false);
    window.location.replace("/");
  };

  const [modalBody, setModalBody] = useState({
    title: "",
    content: "",
  });

  const [languages, setLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') ?? navigator.language);

  // States for email confirmation resend
  const [showResendButton, setShowResendButton] = useState<boolean>(false);
  const [emailUuidForResend, setEmailUuidForResend] = useState<string>("");
  const [resendLoading, setResendLoading] = useState<boolean>(false);

  // Function to resend confirmation email
  const resendConfirmationEmail = async () => {
    if (!emailUuidForResend) return;

    setResendLoading(true);
    try {
      await axios.post(`${environment.api.host}/api/mails/resend-confirmation`, {
        email_uuid: emailUuidForResend
      });

      setErrors({ emailNotConfirmed: [t('login.resend_confirmation.success')] });
      setShowResendButton(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.email?.[0] || t('login.resend_confirmation.error');
      setErrors({ emailNotConfirmed: [errorMessage] });
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    // Get locales from API
    axios
      .get(`${environment.api.host}/api/locales`)
      .then((response) => {
        const locales = response.data.map((locale: any) => {
          return locale.code;
        });

        setLanguages(locales);
      })
      .catch((error) => {
        console.error(`[Login]: Error getting locales`, error);
      });
  }, []);

  const loginSubmit = () => {
    // show loading
    setLoading(true);

    // clean errors
    setErrors({});

    // login axios request
    axios
      .post(`${environment.api.host}/api/auth/login`, {
        email: email,
        password: password,
      })
      .then((response) => {
        // delay to show success
        setTimeout(async () => {
          // hide loading
          setLoading(false);

          // get user
          const user = response.data.items[0];

          if (user.user_setting.change_password) {
            properties.setScene("change-password");
            properties.setUser(user);
            return;
          }

          // save user in local storage
          localStorage.setItem("user", JSON.stringify(user));

          // get user settings
          const userSettings = await UserSettingsHelper.getUserSettings(user);

          // save user settings in local storage
          localStorage.setItem("user_settings", JSON.stringify(userSettings));

          // set user settings in app state
          properties.setUserSettings(userSettings);

          // save canRecord in localStorage
          localStorage.setItem("canRecord", "false");

          // set user in app state
          properties.setUser(user);

                setLoginAttempts(0);

                // Update last login in API
                UserHelper.updateUserLastLogin(user)
                  .then(() => {
                    console.log(`[Login]: Login at ${new Date().toISOString()}`)
                  }).catch((error) => {
                      console.error('Error updating last login', error)
                  });

                await appEventEmitter.emit("login:user", {
                  agent: user.id,
                  user: user,
                });

            }, 200);
        })
            .catch((error) => {

                // delay to show error
                setTimeout(async () => {

                  setLoginAttempts((prev) => prev + 1);

                  if (loginAttempts >= 2) {
                    appEventEmitter.emit("login:failed", {
                      action: 'login',
                      resource: 'user',
                      event_phase: 'failed',
                      agent: email,
                      user: email,
                    })

                    setLoginAttempts(0);
                  }

                  // hide loading
                  setLoading(false)

                  // get response data
                  const responseData = error?.response?.data;

                  // Check if it's an email not confirmed error
                  if (responseData?.emailNotConfirmed) {
                    if (responseData?.tokenExpired) {
                      // Token was expired, email was auto-resent
                      setErrors({ emailNotConfirmed: [responseData?.errors?.emailNotConfirmed?.[0] || t('login.email_not_confirmed_expired')] });
                    } else {
                      // Token still valid, just show message to check inbox
                      setErrors({ emailNotConfirmed: [responseData?.errors?.emailNotConfirmed?.[0] || t('login.email_not_confirmed_pending')] });
                    }
                    // Never show resend button - auto-resend handles expired tokens
                    setShowResendButton(false);
                    return;
                  }

                  // get errors
                  const errors = responseData?.errors;

                  // set request errors
                  if (errors) setErrors(errors)

                  // define general error
                  else setErrors({ general: ['Error de conexión'] })

                }, 200);
            })
    }

  const getAllClients = async (domain?: string) => {
    try {
      const response = await axios.get(
        `${environment.api.host}/api/client/all`,
        {
          params: {
            domain: domain,
          },
        }
      );

      if (!response.data.items) {
        setClients([]);
        return;
      }

      setClients(response.data.items);
    } catch (error) {
      console.error("[Login]: Error getting clients from API", error);

      setClients([]);
    }
  };

  const detectEmailDomain = (email: string) => {
    const domain = email.split("@")[1];

    if (!domain) {
      setEnableProvider(false);
      setProviders([{ id: "", name: "local" }]);
      return;
    }

    if (clients.length > 0) {
      const client = clients.find((client) => {
        return client.auth_providers_clients.find((provider) => {
          return (
            provider.domains.includes(`@${domain}`) ||
            provider.domains.includes(domain)
          );
        });
      });

      if (!client) {
        setEnableProvider(false);
        setProviders([{ id: "", name: "local" }]);
        return;
      }

      const providers = client.auth_providers_clients.filter((provider) => {
        return (
          provider.domains.includes(`@${domain}`) ||
          provider.domains.includes(domain)
        );
      });

      setEnableProvider(true);
      setProviders(
        providers.map((provider) => {
          return {
            id: provider.id,
            name: provider.auth_provider.name,
          };
        })
      );
    } else {
      setEnableProvider(false);
      setProviders([{ id: "", name: "local" }]);
    }
  };

  const handleMicrosoftLogin = async (provider: {
    id: ClientProviders["id"];
    name: AuthProvider["name"];
  }) => {
    try {
      const authProviderId = encodeURIComponent(provider.id);

      // Build Microsoft login URL and redirect to it
      const url = `${environment.api.host}/api/auth/microsoft?auth_provider_id=${authProviderId}&login_hint=${email}`;

      /* @ts-ignore */
      if (window.electron && window.electron.openWindow) {
        /* @ts-ignore */
        const response = await window.electron.openWindow(url);

        if (response && response.success) {
          window.location.replace(response.url);
        } else {
          console.error(
            `[Login]: Error opening Microsoft login window`,
            response.error
          );
        }

        console.log(`[Login]: Microsoft login response`, response);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      console.error(`[Login]: Error getting Microsoft login URL`, error);
    }
  };

  const extractErrors = (data: any) => {
    const extractedErrors = [];

    for (const key in data.errors) {
      if (data.errors.hasOwnProperty(key)) {
        const [title, ...content] = data.errors[key];
        extractedErrors.push({
          type: key,
          title: title || "Error",
          content: content.length > 0 ? content : undefined,
        });
      }
    }

    return extractedErrors;
  };

  const [isAuthMicrosoft, setIsAuthMicrosoft] = useState<boolean>(false);

  useEffect(() => {
    getAllClients();

    if (parsedUrl.controller === "auth" && parsedUrl.action === "microsoft") {
      console.log(`[Login]: Parsed url => ${JSON.stringify(parsedUrl)}`);

      setIsAuthMicrosoft(true);

      if (!parsedUrl.identifiers) {
        console.warn(`[Login]: Microsoft action without identifiers`);
        return;
      }

      switch (parsedUrl.identifiers.type) {
        case "callback":
          console.log(`[Login]: Microsoft callback`);

          const code = parsedUrl.query.get("code") || undefined;
          const session_state =
            parsedUrl.query.get("session_state") || undefined;
          const state = parsedUrl.query.get("state") || undefined;

          // TODO: Send code to API to get user data and authenticate user
          axios
            .get(`${environment.api.host}/api/auth/microsoft/callback`, {
              params: {
                code: code,
                session_state: session_state,
                state: state,
              },
            })
            .then((response) => {
              properties.setIsModalOpen(true);
              properties.setModalType("success");
              properties.setModalBody({
                title: "Éxito",
                content: "Has iniciado sesión correctamente. Redirigiendo...",
              });

              setTimeout(() => {
                window.location.replace(response.data.url);
              }, 2000);
            })
            .catch((error) => {
              console.error(`[Login]: Error getting Microsoft callback`, error);

              const errors = extractErrors(error.response.data);

              if (errors && errors.length > 0) {
                properties.setIsModalOpen(true);
                properties.setModalType("error");
                properties.setModalBody({
                  title: "Error",
                  content: errors
                    .map((error) => {
                      if (error.content) {
                        return `${error.title}: ${error.content.join(", ")}`;
                      }

                      return `${error.title}`;
                    })
                    .join("\n"),
                });

                return;
              }

              properties.setIsModalOpen(true);
              properties.setModalType("error");
              properties.setModalBody({
                title: "Warning",
                content:
                  error.response.data.message ||
                  "Error al iniciar sesión con Microsoft. Por favor, contacte al administrador.",
              });
            });

          break;
        default:
          console.warn(
            `[Login]: Unknown Microsoft action: ${parsedUrl.identifiers.type}`
          );
      }
    }
  }, []);

  return (
    <div
      className="LoginComponent "
      onKeyUp={(e) => {
        if (e.key === "Enter") loginSubmit();
      }}
    >
      { !isAuthMicrosoft && (
        <div className="box loginForm">
          <div>
            <h2>
              {t("login.title.start")} <b>Sippar</b> {t("login.title.end")}
            </h2>
            <ErrorsComponent errors={errors} name="general" />

            <div className="Input">
              <i className="las la-envelope"></i>
              <input
                type="text"
                placeholder={`${t("login.email")}`}
                onChange={(event) => {
                  setEmail(event.target.value);
                  detectEmailDomain(event.target.value);
                }}
              />
            </div>
            <ErrorsComponent errors={errors} name="email" />

            <PasswordInput
              name={"password"}
              providers={providers}
              placeholder={`${t("login.password")}`}
              setPassword={setPassword}
            />
            <ErrorsComponent errors={errors} name="password" />
            <ErrorsComponent errors={errors} name="credentials" />
            <ErrorsComponent errors={errors} name="emailNotConfirmed" />

            {/* Resend Confirmation Email Button */}
            {showResendButton && (
              <div
                className="Submit resend"
                onClick={resendConfirmationEmail}
                style={{ marginTop: 10, backgroundColor: '#007bff' }}
              >
                {resendLoading ? '...' : t("login.resend_confirmation")}
              </div>
            )}

            {/* Local Login */}
            {providers.some((provider) => {
              return provider.name === "local";
            }) && (
              <div className="Submit" onClick={loginSubmit}>
                {t("login.submit")}
              </div>
            )}

            {/* Providers Login */}
            {enableProvider && providers && (
              <div style={{ marginTop: 20 }}>
                {providers.map((provider) => {
                  switch (provider.name) {
                    case "microsoft":
                      return (
                        <div className={`${provider.name}`} key={provider.name}>
                          <MicrosoftButton
                            onClick={(e) => {
                              console.log(`[Login]: Microsoft login`);

                              handleMicrosoftLogin(provider);
                            }}
                          />
                        </div>
                      );

                    case "google":
                      return (
                        <div className={`${provider.name}`} key={provider.name}>
                          <GoogleButton
                            onClick={(e) => {
                              console.log(`[Login]: Google login`);
                            }}
                          />
                        </div>
                      );

                    default:
                      return null;
                  }
                })}
              </div>
            )}

            <div className="Alternative">{t("login.alternative")}</div>
            <div
              className="Submit secondary"
              onClick={() => {
                properties.setScene("register");
              }}
            >
              {t("login.register")}
            </div>
            <div
              className="Alternative link"
              onClick={() => {
                properties.setScene("forgot");
              }}
            >
              {t("login.forgot")}
            </div>
          </div>
          <div>
            <img
              src="images/logo_comitas.png"
              alt="Comitas"
              className="comitas-logo"
              style={{
                width: "150px",
                height: "auto",
                margin: "0 auto",
                display: "block",
              }}
            />
            {/* Change lang selector */}
            <select style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              fontSize: 20,
              padding: '10px',
              borderRadius: '5px',
              border: '1px solid #ccc',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }} onChange={(e) => {
              const lng = e.target.value;
              localStorage.setItem('language', lng);
              i18next.changeLanguage(lng);
              setSelectedLanguage(lng);
            }} value={selectedLanguage}>
              {
                languages.map((lng) => {
                  return <option key={lng} value={lng}>{lng.toLocaleUpperCase()}</option>
                })
              }
            </select>
          </div>
          <LoaderComponent status={loading} />
        </div>
      )}

      {/* Clock to Render in Home Screen */}
      {
        !isAuthMicrosoft && (
          <ClockComponent />
        )
      }
    </div>
  );
}
